import type { RoiId } from '~/domain/changes/ids';
import { vec2, vec3 } from 'gl-matrix';
import { ReactElement, useCallback, useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { useRecoilState, useRecoilValue } from 'recoil';
import { useActiveEditTarget } from '~/hooks/target/useActiveEditTarget';
import {
    type PlaneMm,
    planeDistanceMm,
    planeOffsetMm,
    toPointMm,
} from '~/domain/geometry/patientGeometry';
import { domainEventBus } from '~/domain/events';
import { RoiSelectionRequestedEvent } from '~/domain/events/RoiDomainEvents';
import { getDatasetEnvironmentRegistry } from '~/engine/scene/datasetEnvironmentRegistryInstance';
import type { InteractionContext } from '~/engine/tool/InteractionContext';
import type { StationaryRightClickEvent } from '~/engine/tool/ToolManager';
import { writeRefusal } from '~/engine/tool/tools/contouring-tools/editableView';
import {
    CURVE_PICK_RADIUS_MM,
    voxelWindowAtPoint,
} from '~/engine/view/picking/curveSelectionBox';
import { paneImageOf } from '~/engine/view/paneImage';
import { PlaneView } from '~/engine/view/views/PlaneView';
import { useClearContourExtent } from '~/hooks/structure-set/useClearContourExtent';
import { useClearCurvesInBox } from '~/hooks/structure-set/useClearCurvesInBox';
import { useStructureColorEdit } from '~/hooks/structure-set/useStructureColorEdit';
import {
    contourDeletionMenuState,
    ContourDeletionMenuSource,
    ContourDeletionMenuState,
} from '~/state/atoms/contourDeletionAtoms';
import { selectedROIState } from '~/state/atoms/structureSet/roiAtoms';
import {
    rectangleSelectionState,
    selectedToolState,
} from '~/state/atoms/toolsAtoms';
import { enqueueSnackbar } from '~/utils/notifications';
import type { ClientPoint } from '~/viewportGrid';
import ContourDeletionMenu from './ContourDeletionMenu';

/**
 * Wording for the dragged rectangle. Distinct from the single curve's
 * only in that a drag can take several curves at once, so its report
 * counts them.
 */
const RECTANGLE_MESSAGES = {
    successMessageId: 'postProcessing.delete.rectangleSuccess',
    failedMessageId: 'postProcessing.delete.rectangleFailed',
    wouldEmptyMessageId: 'postProcessing.delete.rectangleWouldEmpty',
    undoLabelId: 'postProcessing.delete.rectangleUndo',
    undoUnavailableMessageId: 'postProcessing.delete.rectangleUndoUnavailable',
    operationName: 'Delete Curves In Rectangle',
    logLabel: 'Delete curves in rectangle',
} as const;

/** Wording for the single picked curve. */
const CURVE_MESSAGES = {
    successMessageId: 'contourDeletion.curve.success',
    failedMessageId: 'contourDeletion.curve.failed',
    wouldEmptyMessageId: 'contourDeletion.curve.wouldEmpty',
    undoLabelId: 'contourDeletion.undo',
    undoUnavailableMessageId: 'contourDeletion.undoUnavailable',
    operationName: 'Delete Contour',
    logLabel: 'Delete contour',
} as const;

/** Wording for the slice and whole-structure clears. */
const EXTENT_MESSAGES = {
    successMessageId: 'contourDeletion.extent.success',
    failedMessageId: 'contourDeletion.extent.failed',
    undoLabelId: 'contourDeletion.undo',
    undoUnavailableMessageId: 'contourDeletion.undoUnavailable',
    logLabel: 'Delete contours',
} as const;

/** How far apart two parallel planes may be and still be one slice, in mm. */
const SAME_PLANE_TOLERANCE_MM = 1e-3;

/**
 * Whether `view` still shows `plane`: the same normal, and a position on
 * the same plane. A pan moves the view within its plane and keeps it; a
 * scroll, a turn or a plane change does not.
 */
function showsPlane(view: PlaneView, plane: PlaneMm): boolean {
    const now = view.plane;
    const alignment = vec3.dot(now.normal, plane.normal);
    if (alignment < 1 - 1e-6) {
        return false;
    }
    const distanceMm = planeDistanceMm(
        toPointMm(now.point),
        plane.normal,
        planeOffsetMm(plane)
    );
    return Math.abs(distanceMm) <= SAME_PLANE_TOLERANCE_MM;
}

/** The contour deletions of a viewer surface: its menu, and how to offer it. */
export interface ContourDeletionsHandle {
    /** The open menu, to render with the surface, or null when none is. */
    menu: ReactElement | null;
    /**
     * Whether a structure label offers the menu over `roiId`: the
     * selected structure only, and only while no menu is open.
     */
    offersFor: (roiId: RoiId) => boolean;
    /**
     * Opens the menu for the selected structure over `view`, when the
     * structure at `pick` is still the selected one. The curve it offers to
     * clear is the one at `pick`; the menu shows at `anchor`. A view of
     * another image than the edit target's is refused with the reason, and
     * never changes the structure set.
     */
    openOverSelected: (
        view: PlaneView,
        pick: ClientPoint,
        anchor: ClientPoint
    ) => void;
}

/**
 * The one owner of a viewer surface's contour deletions: the menu that a
 * still right-click or the hovered structure's label opens, and the
 * rectangle the rectangle erase drags.
 *
 * The menu acts on values snapshot when it opened (the pressed view's
 * plane, the curve under the pointer), and only one is open at a time. It
 * closes on anything that makes the snapshot stale: a scroll of the edit
 * target, a registry change, a selection or tool change, a window blur.
 * An item checks once more that the pressed view still shows the snapshot
 * plane, and refuses with the reason when it does not, so no deletion lands
 * on a slice the user is not looking at. The slice clear also needs the
 * pressed view to draw the edited image, alone or blended: withheld at
 * open, and checked again at the click.
 *
 * @param interactionContext The surface's interaction context, whose still
 *     right-clicks open the menu; undefined while the surface has none.
 */
export function useContourDeletions(
    interactionContext: InteractionContext | undefined
): ContourDeletionsHandle {
    const intl = useIntl();
    const { environment } = useActiveEditTarget();
    const selectedROI = useRecoilValue(selectedROIState);
    const selectedTool = useRecoilValue(selectedToolState);
    const [menu, setMenu] = useRecoilState(contourDeletionMenuState);
    const [rectangleSelection, setRectangleSelection] = useRecoilState(
        rectangleSelectionState
    );
    /**
     * The view the open menu was asked from. Held here and not in the
     * atom, which Recoil freezes: an engine view must stay mutable.
     */
    const menuViewRef = useRef<PlaneView | undefined>(undefined);
    /** Whether the view the open menu was asked from drew the edited image then. */
    const [pressedViewShowsImage, setPressedViewShowsImage] = useState(false);

    const { clearCurvesInBox } = useClearCurvesInBox(CURVE_MESSAGES);
    const { clearContourExtent } = useClearContourExtent(EXTENT_MESSAGES);
    const { clearCurvesInBox: clearCurvesInRectangle } =
        useClearCurvesInBox(RECTANGLE_MESSAGES);

    const closeMenu = useCallback(() => {
        menuViewRef.current = undefined;
        setMenu(null);
    }, [setMenu]);
    const { open: openColorEdit } = useStructureColorEdit();

    // ── Opening ──────────────────────────────────────────────────────

    const openAt = useCallback(
        (
            view: PlaneView,
            pick: ClientPoint,
            source: ContourDeletionMenuSource,
            anchor: ClientPoint
        ) => {
            const refusal = writeRefusal(view, environment);
            if (refusal !== undefined) {
                enqueueSnackbar(refusal, { variant: 'warning' });
                return;
            }
            // writeRefusal refuses every view while no Environment is armed.
            if (!environment) {
                return;
            }
            const coordinates = vec2.fromValues(pick.clientX, pick.clientY);
            const patientCoords = view.clientToPatientCoordinates(coordinates);
            const texture =
                selectedROI === null
                    ? undefined
                    : environment.structureSet?.getRoi(selectedROI)?.texture;
            // What the pointer was over, which may be a neighbour's
            // contour: the menu reports it and offers to select it, but
            // never acts on it. Looked up within the curve pick radius, the
            // distance at which the hover label names a structure and the
            // curve delete picks a curve.
            const pickedRoiId = environment.structureSet?.findRoiWithinMm(
                patientCoords,
                CURVE_PICK_RADIUS_MM
            )?.roiId;
            // A label offers the menu over the selected structure only; a
            // label pick that no longer resolves to it opens nothing.
            if (source === 'hover-affordance' && pickedRoiId !== selectedROI) {
                return;
            }
            menuViewRef.current = view;
            setPressedViewShowsImage(paneImageOf(view).kind === 'primary');
            setMenu({
                anchorPosition: { left: anchor.clientX, top: anchor.clientY },
                source,
                // The plane of the view PRESSED ON: a right-click on a
                // sagittal tile must not clear a transversal slice.
                planeAtOpen: view.plane,
                pickedRoiId,
                // Resolved now, against the selected ROI's own grid, so the
                // item is disabled before any labeling pass runs. Undefined
                // when no contour is within the pick radius.
                curveWindow: texture
                    ? voxelWindowAtPoint(view, texture, patientCoords)
                    : undefined,
            } satisfies ContourDeletionMenuState);
        },
        [environment, selectedROI, setMenu]
    );

    useEffect(() => {
        if (!interactionContext) {
            return undefined;
        }
        const events = interactionContext.toolManager.eventEmitter;
        const onStationaryRightClick = ({
            coordinates,
            view,
        }: StationaryRightClickEvent) => {
            if (view instanceof PlaneView) {
                const pressed = {
                    clientX: coordinates[0],
                    clientY: coordinates[1],
                };
                openAt(view, pressed, 'right-click', pressed);
            }
        };
        events.on('stationaryRightClick', onStationaryRightClick);
        return () => {
            events.off('stationaryRightClick', onStationaryRightClick);
        };
    }, [interactionContext, openAt]);

    const openOverSelected = useCallback(
        (view: PlaneView, pick: ClientPoint, anchor: ClientPoint) =>
            openAt(view, pick, 'hover-affordance', anchor),
        [openAt]
    );

    const menuOpen = menu !== null;
    const offersFor = useCallback(
        (roiId: RoiId) => roiId === selectedROI && !menuOpen,
        [selectedROI, menuOpen]
    );

    // ── Closing on anything that makes the snapshot stale ────────────

    useEffect(() => {
        closeMenu();
    }, [selectedROI, environment, selectedTool, closeMenu]);

    useEffect(() => {
        if (!environment) {
            return undefined;
        }
        const onScroll = () => closeMenu();
        environment.planeIntersectionPoint.subscribe(onScroll);
        return () => environment.planeIntersectionPoint.unsubscribe(onScroll);
    }, [environment, closeMenu]);

    useEffect(
        () => getDatasetEnvironmentRegistry().subscribeLifecycle(closeMenu),
        [closeMenu]
    );

    useEffect(() => {
        window.addEventListener('blur', closeMenu);
        return () => window.removeEventListener('blur', closeMenu);
    }, [closeMenu]);

    // ── The dragged rectangle ────────────────────────────────────────
    //
    // Takes each drag the rectangle erase hands over through the atom,
    // and clears the atom straight back to null. No drag outlives this
    // owner: one that lands as it unmounts is dropped, never replayed on
    // the next mount against whatever structure is selected by then.
    useEffect(() => {
        if (!rectangleSelection) {
            return;
        }
        setRectangleSelection(null);
        void (async () => {
            const outcome = await clearCurvesInRectangle(
                rectangleSelection.range
            );
            if (outcome === 'nothing') {
                enqueueSnackbar(
                    intl.formatMessage({
                        id: 'postProcessing.delete.rectangleNothingInside',
                    }),
                    { variant: 'info' }
                );
            }
        })();
    }, [
        rectangleSelection,
        setRectangleSelection,
        clearCurvesInRectangle,
        intl,
    ]);

    useEffect(() => () => setRectangleSelection(null), [setRectangleSelection]);

    // ── Acting ───────────────────────────────────────────────────────

    /**
     * Whether the view the menu was asked from still shows the slice it
     * showed then; warns and closes the menu when it does not.
     */
    const confirmSnapshotPlane = useCallback((): boolean => {
        const view = menuViewRef.current;
        const plane = menu?.planeAtOpen;
        if (view && plane && showsPlane(view, plane)) {
            return true;
        }
        closeMenu();
        enqueueSnackbar(
            intl.formatMessage({ id: 'contourDeletion.viewChanged' }),
            { variant: 'warning' }
        );
        return false;
    }, [menu, closeMenu, intl]);

    const handleSelectPicked = useCallback(
        (roiId: RoiId) => {
            closeMenu();
            // Selects through the domain selection request, the one path
            // every structure selection takes.
            void domainEventBus
                .publish(new RoiSelectionRequestedEvent(roiId, 'user'))
                .catch((error: unknown) => {
                    console.error('Failed to select structure', error);
                });
        },
        [closeMenu]
    );

    const handleDeleteCurve = useCallback(async () => {
        const range = menu?.curveWindow;
        if (!range) {
            closeMenu();
            return;
        }
        if (!confirmSnapshotPlane()) {
            return;
        }
        closeMenu();
        const outcome = await clearCurvesInBox(range);
        if (outcome === 'nothing') {
            // A toast, not an inline line: the menu that asked for this is
            // gone by the time the answer arrives.
            enqueueSnackbar(
                intl.formatMessage({ id: 'contourDeletion.curve.nothing' }),
                { variant: 'info' }
            );
        }
    }, [menu, confirmSnapshotPlane, closeMenu, clearCurvesInBox, intl]);

    const handleClearSlice = useCallback(async () => {
        const plane = menu?.planeAtOpen ?? null;
        if (!confirmSnapshotPlane()) {
            return;
        }
        const view = menuViewRef.current;
        closeMenu();
        if (!view || paneImageOf(view).kind !== 'primary') {
            enqueueSnackbar(
                intl.formatMessage({
                    id: 'postProcessing.delete.tileShowsOtherImage',
                }),
                { variant: 'warning' }
            );
            return;
        }
        // Committed with no result checked first, so the report carries
        // the way back.
        await clearContourExtent({
            scope: 'this-slice',
            plane,
            addressedBy: 'view',
            offerUndo: true,
        });
    }, [menu, confirmSnapshotPlane, closeMenu, clearContourExtent, intl]);

    const handleClearStructure = useCallback(async () => {
        closeMenu();
        await clearContourExtent({ scope: 'all-slices', offerUndo: true });
    }, [closeMenu, clearContourExtent]);

    // Opens beside the point the menu was opened at. There is no control
    // to return focus to: the menu that asked is gone.
    const handleChangeColor = useCallback(() => {
        const at = menu?.anchorPosition;
        closeMenu();
        if (!at || selectedROI === null) {
            return;
        }
        const anchor = {
            getBoundingClientRect: () => new DOMRect(at.left, at.top),
        };
        openColorEdit(selectedROI, 'viewer', anchor, null);
    }, [menu, closeMenu, openColorEdit, selectedROI]);

    return {
        menu: menu ? (
            <ContourDeletionMenu
                menu={menu}
                pressedViewShowsImage={pressedViewShowsImage}
                onClose={closeMenu}
                onSelectPicked={handleSelectPicked}
                onDeleteCurve={() => void handleDeleteCurve()}
                onClearSlice={() => void handleClearSlice()}
                onClearStructure={() => void handleClearStructure()}
                onChangeColor={handleChangeColor}
            />
        ) : null,
        offersFor,
        openOverSelected,
    };
}
